import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { ArrowRight, Plus, Minus, Star } from "lucide-react";
import { SITE } from "../data/mock";
import { HeroVideo } from "./HeroVideo";

// Official four-color Google "G" mark
function GoogleG({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

// Google Business Profile review CTAs — used wherever GBP reviews are featured
export function GoogleReviewCta({ className = "" }) {
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      <a
        href={SITE.gbpUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="google-reviews-link"
        className="inline-flex items-center gap-2.5 border border-white/15 hover:border-white/40 text-neutral-200 hover:text-white font-mono text-[11px] uppercase tracking-[0.14em] px-4 py-2.5 transition-colors"
      >
        <GoogleG /> Read our Google reviews
      </a>
      <a
        href={SITE.reviewUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="google-write-review-link"
        className="inline-flex items-center gap-2 bg-[#9D0423] hover:bg-[#7E031C] text-white font-mono font-semibold text-[11px] uppercase tracking-[0.14em] px-4 py-2.5 transition-colors"
      >
        <Star size={13} className="fill-white" /> Leave us a review
      </a>
    </div>
  );
}

// Scroll restoration on route change (with hash anchor support)
export function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
        else window.scrollTo(0, 0);
      }, 150);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

// Scroll-triggered reveal wrapper
export function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 0.7s ease ${delay}ms, transform 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

// Mono eyebrow label
export function Eyebrow({ children, light = false }) {
  return (
    <p className={`font-mono text-[11px] uppercase tracking-[0.3em] mb-4 ${light ? "text-neutral-500" : "text-[#9D0423]"}`}>
      // {children}
    </p>
  );
}

// Primary / secondary buttons
export function BtnPrimary({ to, href, children, className = "", ...rest }) {
  const cls = `inline-flex items-center justify-center gap-2 bg-[#9D0423] hover:bg-[#7E031C] text-white font-mono font-semibold text-[13px] uppercase tracking-[0.14em] px-7 py-4 transition-all duration-200 hover:translate-y-[-2px] hover:shadow-[0_10px_30px_rgba(157,4,35,0.38)] ${className}`;
  if (href)
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls} {...rest}>
        {children} <ArrowRight size={15} />
      </a>
    );
  return (
    <Link to={to} className={cls} {...rest}>
      {children} <ArrowRight size={15} />
    </Link>
  );
}

export function BtnGhost({ to, href, children, className = "", ...rest }) {
  const cls = `inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white/60 text-white font-mono font-semibold text-[13px] uppercase tracking-[0.14em] px-7 py-4 transition-colors duration-200 ${className}`;
  if (href)
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls} {...rest}>
        {children}
      </a>
    );
  return (
    <Link to={to} className={cls} {...rest}>
      {children}
    </Link>
  );
}

// Page hero for interior pages
export function PageHero({ label, title, sub, image, video }) {
  return (
    <section className="relative bg-[#0A0A0B] overflow-hidden">
      {image && (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${image})` }}
            aria-hidden="true"
          />
          {video && <HeroVideo base={video} />}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-[#0A0A0B]/85 to-[#0A0A0B]/40" aria-hidden="true" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent" aria-hidden="true" />
        </>
      )}
      <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28">
        <Reveal>
          <Eyebrow>{label}</Eyebrow>
          <h1 className="font-heading font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tight max-w-3xl">
            {title}
          </h1>
          {sub && <p className="mt-6 text-neutral-300 text-base lg:text-lg leading-relaxed max-w-2xl">{sub}</p>}
        </Reveal>
      </div>
    </section>
  );
}

// FAQ accordion (custom, dark-styled)
export function FaqList({ faqs, title = "Frequently Asked Questions" }) {
  const [open, setOpen] = useState(0);
  return (
    <div>
      <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white tracking-tight mb-8">{title}</h2>
      <div className="divide-y divide-white/10 border-y border-white/10">
        {faqs.map((f, i) => (
          <div key={i}>
            <button
              onClick={() => setOpen(open === i ? -1 : i)}
              className="w-full flex items-center justify-between gap-6 py-5 text-left group"
              aria-expanded={open === i}
              data-testid={`faq-item-${i}`}
            >
              <span className="text-[15px] lg:text-base font-semibold text-neutral-200 group-hover:text-white transition-colors">{f.q}</span>
              <span className="shrink-0 text-[#9D0423]">{open === i ? <Minus size={18} /> : <Plus size={18} />}</span>
            </button>
            <div
              className="overflow-hidden transition-[max-height,opacity] duration-300 ease-out"
              style={{ maxHeight: open === i ? "300px" : "0px", opacity: open === i ? 1 : 0 }}
            >
              <p className="pb-6 pr-10 text-[15px] text-neutral-400 leading-relaxed">{f.a}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// CTA band used across pages
export function CtaBand({ title = "Not sure where to start?", sub = "Book a free goal review — a 20-minute conversation about your goals, history and the right first step. No pressure, no obligation." }) {
  return (
    <section className="relative bg-[#9D0423] overflow-hidden">
      <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_20%_50%,white_1px,transparent_1px)] [background-size:24px_24px]" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-20 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
        <div className="max-w-2xl">
          <h2 className="font-heading font-extrabold uppercase text-3xl sm:text-4xl text-white leading-tight tracking-tight">{title}</h2>
          <p className="mt-4 text-white/85 text-base leading-relaxed">{sub}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 shrink-0">
          <Link
            to="/get-started"
            className="inline-flex items-center justify-center gap-2 bg-[#0A0A0B] hover:bg-black text-white font-mono font-semibold text-[13px] uppercase tracking-[0.14em] px-8 py-4 transition-all duration-200 hover:translate-y-[-2px]"
          >
            Book Free Goal Review <ArrowRight size={15} />
          </Link>
          <Link
            to="/schedule"
            className="inline-flex items-center justify-center border-2 border-white/60 hover:border-white text-white font-mono font-semibold text-[13px] uppercase tracking-[0.14em] px-8 py-4 transition-colors"
          >
            View Schedule
          </Link>
        </div>
      </div>
    </section>
  );
}

// Simple breadcrumb display
export function Crumbs({ items }) {
  return (
    <nav aria-label="Breadcrumb" className="font-mono text-[11px] uppercase tracking-[0.14em] text-neutral-500 flex items-center gap-2 flex-wrap">
      {items.map((c, i) => (
        <React.Fragment key={c.path}>
          {i > 0 && <span className="text-neutral-700">/</span>}
          {i === items.length - 1 ? (
            <span className="text-neutral-300">{c.name}</span>
          ) : (
            <Link to={c.path} className="hover:text-white transition-colors">{c.name}</Link>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}
